import React from 'react';
import { useTranslation } from 'react-i18next';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';

function safeColorInputValue(value) {
  return /^#[0-9A-Fa-f]{6}$/.test(String(value || '')) ? value : '#0d6efd';
}

function SystemGeneralSettingsPageView({
  form,
  isLoading,
  isSaving,
  isLogoUploading,
  isLogoDeleting,
  error,
  success,
  formErrors,
  hasChanges,
  selectedLogoFile,
  logoInputKey,
  onFormChange,
  onLogoFileChange,
  uploadLogo,
  deleteLogo,
  resetForm,
  saveForm,
  mode = 'general',
}) {
  const { t } = useTranslation();
  const isBusy = isSaving || isLogoUploading || isLogoDeleting;
  const isBrandingMode = mode === 'branding';

  return (
    <div>
      <FloatingAlerts
        info={isBusy ? t('generalSettings.savingInfo') : ''}
        error={error}
        success={success}
      />
      <PageHeader
        title={t(isBrandingMode ? 'generalSettings.brandingTitle' : 'generalSettings.title')}
        subtitle={t(isBrandingMode ? 'generalSettings.brandingSubtitle' : 'generalSettings.subtitle')}
      />

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <LoadingContainer isLoading={isLoading} hasData className="">
            <form onSubmit={saveForm}>
              <div className="row g-3">
                {!isBrandingMode ? (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">{t('generalSettings.complianceModeLabel')}</label>
                      <select
                        className={`form-select ${formErrors.complianceMode ? 'is-invalid' : ''}`}
                        value={form.complianceMode}
                        onChange={(event) => onFormChange('complianceMode', event.target.value)}
                        disabled={isBusy}
                      >
                        <option value="gdpr">GDPR</option>
                        <option value="financial_regulation">Financial Regulation</option>
                      </select>
                      {formErrors.complianceMode ? <div className="invalid-feedback">{formErrors.complianceMode}</div> : null}
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">{t('generalSettings.dpoNameLabel')}</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.dpoName ? 'is-invalid' : ''}`}
                        value={form.dpoName}
                        onChange={(event) => onFormChange('dpoName', event.target.value)}
                        disabled={isBusy}
                      />
                      {formErrors.dpoName ? <div className="invalid-feedback">{formErrors.dpoName}</div> : null}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('generalSettings.dpoEmailLabel')}</label>
                      <input
                        type="email"
                        className={`form-control ${formErrors.dpoEmail ? 'is-invalid' : ''}`}
                        value={form.dpoEmail}
                        onChange={(event) => onFormChange('dpoEmail', event.target.value)}
                        disabled={isBusy}
                      />
                      {formErrors.dpoEmail ? <div className="invalid-feedback">{formErrors.dpoEmail}</div> : null}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('generalSettings.dpoPhoneLabel')}</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.dpoPhone ? 'is-invalid' : ''}`}
                        value={form.dpoPhone}
                        onChange={(event) => onFormChange('dpoPhone', event.target.value)}
                        disabled={isBusy}
                      />
                      {formErrors.dpoPhone ? <div className="invalid-feedback">{formErrors.dpoPhone}</div> : null}
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">{t('generalSettings.dataCenterCountryLabel')}</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.dataCenterCountry ? 'is-invalid' : ''}`}
                        value={form.dataCenterCountry}
                        onChange={(event) => onFormChange('dataCenterCountry', event.target.value)}
                        disabled={isBusy}
                      />
                      {formErrors.dataCenterCountry ? <div className="invalid-feedback">{formErrors.dataCenterCountry}</div> : null}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('generalSettings.dataCenterNameLabel')}</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.dataCenterName ? 'is-invalid' : ''}`}
                        value={form.dataCenterName}
                        onChange={(event) => onFormChange('dataCenterName', event.target.value)}
                        disabled={isBusy}
                      />
                      {formErrors.dataCenterName ? <div className="invalid-feedback">{formErrors.dataCenterName}</div> : null}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('generalSettings.dataCenterAddressLabel')}</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.dataCenterAddress ? 'is-invalid' : ''}`}
                        value={form.dataCenterAddress}
                        onChange={(event) => onFormChange('dataCenterAddress', event.target.value)}
                        disabled={isBusy}
                      />
                      {formErrors.dataCenterAddress ? <div className="invalid-feedback">{formErrors.dataCenterAddress}</div> : null}
                    </div>

                    <div className="col-12">
                      <label className="form-label">{t('generalSettings.allowedStorageRegionsLabel')}</label>
                      <textarea
                        rows="4"
                        className={`form-control ${formErrors.allowedStorageRegionsText ? 'is-invalid' : ''}`}
                        value={form.allowedStorageRegionsText}
                        onChange={(event) => onFormChange('allowedStorageRegionsText', event.target.value)}
                        disabled={isBusy}
                        placeholder={t('generalSettings.allowedStorageRegionsPlaceholder')}
                      />
                      {formErrors.allowedStorageRegionsText ? <div className="invalid-feedback">{formErrors.allowedStorageRegionsText}</div> : null}
                    </div>
                  </>
                ) : null}

                {isBrandingMode ? (
                  <>
                    <div className="col-lg-4">
                      <label className="form-label">{t('generalSettings.logoLabel')}</label>
                      <div className="border rounded bg-light d-flex align-items-center justify-content-center mb-2" style={{ minHeight: '112px' }}>
                        {form.logoUrl ? (
                          <img
                            src={form.logoUrl}
                            alt={t('generalSettings.logoPreviewAlt')}
                            className="img-fluid"
                            style={{ maxHeight: '96px', maxWidth: '100%', objectFit: 'contain' }}
                          />
                        ) : (
                          <span className="text-muted">{t('generalSettings.logoEmpty')}</span>
                        )}
                      </div>
                      <input
                        key={logoInputKey}
                        type="file"
                        className={`form-control ${formErrors.logo ? 'is-invalid' : ''}`}
                        accept="image/png,image/jpeg,image/webp"
                        onChange={(event) => onLogoFileChange(event.target.files?.[0] || null)}
                        disabled={isBusy}
                      />
                      {formErrors.logo ? <div className="invalid-feedback">{formErrors.logo}</div> : null}
                      <div className="form-text">{t('generalSettings.logoHelp')}</div>
                      <div className="d-flex flex-wrap gap-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={uploadLogo}
                          disabled={isBusy || !selectedLogoFile}
                        >
                          {t('generalSettings.uploadLogo')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-default"
                          onClick={deleteLogo}
                          disabled={isBusy || !form.logoUrl}
                        >
                          {t('generalSettings.removeLogo')}
                        </button>
                      </div>
                    </div>

                    <div className="col-lg-8">
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">{t('generalSettings.brandNameLabel')}</label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.appName ? 'is-invalid' : ''}`}
                            value={form.appName}
                            onChange={(event) => onFormChange('appName', event.target.value)}
                            disabled={isBusy}
                            placeholder="PnP FX"
                          />
                          {formErrors.appName ? <div className="invalid-feedback">{formErrors.appName}</div> : null}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">{t('generalSettings.primaryColorLabel')}</label>
                          <div className="input-group">
                            <input
                              type="color"
                              className="form-control form-control-color"
                              value={safeColorInputValue(form.primaryColor)}
                              onChange={(event) => onFormChange('primaryColor', event.target.value)}
                              disabled={isBusy}
                              title={t('generalSettings.primaryColorLabel')}
                            />
                            <input
                              type="text"
                              className={`form-control ${formErrors.primaryColor ? 'is-invalid' : ''}`}
                              value={form.primaryColor}
                              onChange={(event) => onFormChange('primaryColor', event.target.value)}
                              disabled={isBusy}
                              placeholder="#0d6efd"
                            />
                            {formErrors.primaryColor ? <div className="invalid-feedback">{formErrors.primaryColor}</div> : null}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">{t('generalSettings.accentColorLabel')}</label>
                          <div className="input-group">
                            <input
                              type="color"
                              className="form-control form-control-color"
                              value={safeColorInputValue(form.accentColor)}
                              onChange={(event) => onFormChange('accentColor', event.target.value)}
                              disabled={isBusy}
                              title={t('generalSettings.accentColorLabel')}
                            />
                            <input
                              type="text"
                              className={`form-control ${formErrors.accentColor ? 'is-invalid' : ''}`}
                              value={form.accentColor}
                              onChange={(event) => onFormChange('accentColor', event.target.value)}
                              disabled={isBusy}
                              placeholder="#0d6efd"
                            />
                            {formErrors.accentColor ? <div className="invalid-feedback">{formErrors.accentColor}</div> : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={resetForm}
                  disabled={isBusy || !hasChanges}
                >
                  {t('generalSettings.reset')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading || isBusy || !hasChanges}
                >
                  {t('generalSettings.saveChanges')}
                </button>
              </div>
            </form>
          </LoadingContainer>
        </div>
      </div>
    </div>
  );
}

export default SystemGeneralSettingsPageView;
